/**
 * Rosemont Printing: artwork uploads for the Get a Quote form.
 *
 * Files are sent in 8 MB parts to the upload Worker (Cloudflare Workers + R2, see upload-worker/).
 * When a file finishes, the Worker returns a download URL; all finished files are written to the
 * hidden "contact[Artwork files]" field, so the links arrive in the quote email.
 * Nothing is base64-encoded and no file content is sent through the Shopify form.
 *
 * Markup: snippets/ps-artwork-upload.liquid
 */
(() => {
  const ICON_CLOSE = '<svg class="ps-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';

  const formatSize = (bytes) => {
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} B`;
  };
  const extensionOf = (name) => {
    const m = /\.([a-z0-9]+)$/i.exec(name || '');
    return m ? m[1].toLowerCase() : '';
  };

  class ArtworkUpload {
    constructor(root) {
      this.root = root;
      this.form = root.closest('form');
      this.endpoint = (root.dataset.endpoint || '').replace(/\/+$/, '');
      this.maxFiles = Number(root.dataset.maxFiles || 5);
      this.maxBytes = Number(root.dataset.maxMb || 100) * 1024 * 1024;
      this.accept = (root.dataset.accept || '').split(',').map((e) => e.trim()).filter(Boolean);
      this.input = root.querySelector('[data-upload-input]');
      this.drop = root.querySelector('[data-upload-drop]');
      this.list = root.querySelector('[data-upload-list]');
      this.error = root.querySelector('[data-upload-error]');
      this.value = root.querySelector('[data-upload-value]');
      this.items = [];
      this.nextId = 1;
      this.bind();
    }

    bind() {
      this.input.addEventListener('change', () => {
        this.addFiles([...this.input.files]);
        this.input.value = '';
      });
      ['dragenter', 'dragover'].forEach((type) =>
        this.drop.addEventListener(type, (e) => {
          e.preventDefault();
          this.drop.classList.add('is-dragover');
        })
      );
      ['dragleave', 'dragend', 'drop'].forEach((type) =>
        this.drop.addEventListener(type, (e) => {
          e.preventDefault();
          if (type !== 'dragleave' || !this.drop.contains(e.relatedTarget)) this.drop.classList.remove('is-dragover');
        })
      );
      this.drop.addEventListener('drop', (e) => {
        if (e.dataTransfer && e.dataTransfer.files) this.addFiles([...e.dataTransfer.files]);
      });
      // Capture phase so this check runs before other submit handlers on the form.
      if (this.form) this.form.addEventListener('submit', (e) => this.onSubmit(e), true);
    }

    showError(messages) {
      const text = [].concat(messages).filter(Boolean).join('\n');
      this.error.textContent = text;
      this.error.hidden = !text;
    }

    addFiles(files) {
      const errors = [];
      files.forEach((file) => {
        const active = this.items.filter((i) => i.status !== 'error').length;
        const ext = extensionOf(file.name);
        if (active >= this.maxFiles) {
          errors.push(`You can attach up to ${this.maxFiles} files. "${file.name}" was not added.`);
        } else if (!this.accept.includes(ext)) {
          errors.push(`"${file.name}" is not an accepted file type (PNG, JPG, JPEG, GIF, PDF, AI, EPS, SVG, PSD).`);
        } else if (file.size > this.maxBytes) {
          errors.push(`"${file.name}" is ${formatSize(file.size)}. The limit is ${formatSize(this.maxBytes)} per file. For larger files, paste a share link below.`);
        } else if (file.size === 0) {
          errors.push(`"${file.name}" is empty.`);
        } else {
          const item = { id: this.nextId++, file, ext, status: 'queued', loaded: 0, url: null };
          this.items.push(item);
          this.renderItem(item);
          this.upload(item);
        }
      });
      this.showError(errors);
    }

    renderItem(item) {
      const li = document.createElement('li');
      li.className = 'ps-upload__item';
      li.innerHTML = `
        <span class="ps-upload__item-icon" aria-hidden="true"></span>
        <span class="ps-upload__item-body">
          <span class="ps-upload__item-name"></span>
          <span class="ps-upload__bar" aria-hidden="true"><span></span></span>
          <span class="ps-upload__item-meta"></span>
        </span>
        <span class="ps-upload__item-actions">
          <button type="button" class="ps-upload__icon-btn" data-retry hidden>Retry</button>
          <button type="button" class="ps-upload__icon-btn" data-remove>${ICON_CLOSE}</button>
        </span>`;
      li.querySelector('.ps-upload__item-icon').textContent = item.ext;
      li.querySelector('.ps-upload__item-name').textContent = item.file.name;
      li.querySelector('[data-remove]').setAttribute('aria-label', `Remove ${item.file.name}`);
      li.querySelector('[data-remove]').addEventListener('click', () => this.remove(item));
      li.querySelector('[data-retry]').addEventListener('click', () => this.upload(item));
      item.el = li;
      this.list.appendChild(li);
      this.updateItem(item);
    }

    updateItem(item) {
      const el = item.el;
      if (!el) return;
      const pct = item.file.size ? Math.min(100, Math.round((item.loaded / item.file.size) * 100)) : 0;
      el.classList.toggle('is-done', item.status === 'done');
      el.classList.toggle('is-error', item.status === 'error');
      el.querySelector('.ps-upload__bar span').style.width = `${item.status === 'done' ? 100 : pct}%`;
      el.querySelector('[data-retry]').hidden = item.status !== 'error';
      const size = formatSize(item.file.size);
      const meta = {
        queued: `${size} · Waiting…`,
        uploading: `${size} · Uploading ${pct}%`,
        done: `${size} · Uploaded ✓`,
        error: `${size} · Upload failed${item.message ? `: ${item.message}` : ''}`,
      }[item.status];
      el.querySelector('.ps-upload__item-meta').textContent = meta;
    }

    async request(path, options) {
      const res = await fetch(`${this.endpoint}${path}`, options);
      let data = {};
      try {
        data = await res.json();
      } catch (e) {
        /* non-JSON error */
      }
      if (!res.ok) throw new Error(data.error || `Server error ${res.status}`);
      return data;
    }

    putPart(item, url, blob, baseLoaded) {
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        item.xhr = xhr;
        xhr.open('PUT', url);
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            item.loaded = baseLoaded + e.loaded;
            this.updateItem(item);
          }
        };
        xhr.onload = () => {
          let data = {};
          try {
            data = JSON.parse(xhr.responseText);
          } catch (e) {
            /* ignore */
          }
          if (xhr.status >= 200 && xhr.status < 300) resolve(data);
          else reject(new Error(data.error || `Server error ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.onabort = () => reject(new Error('aborted'));
        xhr.send(blob);
      });
    }

    async upload(item) {
      item.status = 'uploading';
      item.loaded = 0;
      item.message = '';
      item.cancelled = false;
      this.updateItem(item);
      this.syncValue();
      try {
        const created = await this.request('/upload/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: item.file.name, size: item.file.size, type: item.file.type }),
        });
        item.key = created.key;
        item.uploadId = created.uploadId;
        const partSize = created.partSize;
        const parts = [];
        for (let start = 0, n = 1; start < item.file.size; start += partSize, n++) {
          if (item.cancelled) throw new Error('aborted');
          const blob = item.file.slice(start, Math.min(start + partSize, item.file.size));
          const query = `?key=${encodeURIComponent(item.key)}&uploadId=${encodeURIComponent(item.uploadId)}&part=${n}`;
          const part = await this.putPart(item, `${this.endpoint}/upload/part${query}`, blob, start);
          parts.push({ partNumber: part.partNumber, etag: part.etag });
        }
        const done = await this.request('/upload/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ key: item.key, uploadId: item.uploadId, parts }),
        });
        item.url = done.url;
        item.status = 'done';
        item.loaded = item.file.size;
      } catch (error) {
        if (item.cancelled || error.message === 'aborted') return;
        item.status = 'error';
        item.message = error.message;
        this.abortRemote(item);
      }
      this.updateItem(item);
      this.syncValue();
    }

    abortRemote(item) {
      if (!item.key || !item.uploadId) return;
      fetch(`${this.endpoint}/upload/abort`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: item.key, uploadId: item.uploadId }),
        keepalive: true,
      }).catch(() => {});
    }

    remove(item) {
      if (item.status === 'uploading' || item.status === 'queued') {
        item.cancelled = true;
        if (item.xhr) item.xhr.abort();
        this.abortRemote(item);
      }
      this.items = this.items.filter((i) => i !== item);
      if (item.el) item.el.remove();
      this.syncValue();
      this.showError([]);
    }

    syncValue() {
      const done = this.items.filter((i) => i.status === 'done' && i.url);
      this.value.value = done
        .map((i, index) => `${index + 1}) ${i.file.name} (${formatSize(i.file.size)}): ${i.url}`)
        .join('\n');
    }

    onSubmit(event) {
      const busy = this.items.some((i) => i.status === 'uploading' || i.status === 'queued');
      if (busy) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError('Please wait until your files finish uploading, then submit again.');
        this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const failed = this.items.filter((i) => i.status === 'error');
      if (failed.length) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError('Some files did not upload. Click Retry, or remove them before submitting.');
        this.root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.syncValue();
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ps-upload]').forEach((root) => {
      if (root.psUpload || !root.dataset.endpoint) return;
      root.psUpload = new ArtworkUpload(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
